import type { ComputedRef, Ref } from 'vue';

import { computed, ref } from 'vue';

// Barreira fisica do drawer.
// Quando armada, o browser nao consegue rolar acima do topo do documento (real 0).
// A barreira faz com que o topo real do documento coincida com a ancora do fullscreen
// (spacerExtent), encolhendo o spacer para 0.
// Quando desarmada, o spacer tem o tamanho total (spacerExtent), e o real 0 coincide
// com a base do sheet (FULLY_OPEN).
export interface PhysicalBarrier {
	isArmed: Ref<boolean>;
	spacerHeight: ComputedRef<number>;
	toReal(virtual: number): number;
	toVirtual(real: number): number;
	setArmed(armed: boolean, currentVisual: number): void;
}

export function usePhysicalBarrier(
	spacerExtent: Ref<number>,
	spacerElement?: Ref<HTMLElement | null>
): PhysicalBarrier {
	const isArmed = ref(false);

	const barrierOffset = computed(() =>
		isArmed.value ? spacerExtent.value : 0
	);

	const spacerHeight = computed(() =>
		Math.max(0, spacerExtent.value - barrierOffset.value)
	);

	// Converte Y virtual -> Y real da janela.
	function toReal(virtual: number): number {
		return Math.max(0, virtual - barrierOffset.value);
	}

	// Converte Y real da janela -> Y virtual.
	function toVirtual(real: number): number {
		return real + barrierOffset.value;
	}

	// Troca o estado da barreira preservando a posicao VISUAL.
	// A altura nova do spacer e o scrollTo precisam entrar no MESMO commit de layout.
	function setArmed(armed: boolean, currentVisual: number): void {
		if (isArmed.value === armed) return;
		isArmed.value = armed;

		const offset = armed ? spacerExtent.value : 0;
		const spacerEl = spacerElement?.value;
		if (spacerEl) {
			const nextSpacerHeight = Math.max(0, spacerExtent.value - offset);
			spacerEl.style.height = `${nextSpacerHeight}px`;
			// Força um reflow síncrono leve para que o browser atualize as dimensões do documento
			// antes de executarmos o window.scrollTo abaixo, evitando clamping incorreto do scroll.
			void spacerEl.offsetHeight;
		}
		window.scrollTo(0, Math.max(0, currentVisual - offset));
	}

	return {
		isArmed,
		spacerHeight,
		toReal,
		toVirtual,
		setArmed
	};
}
